import React, { useEffect, useRef, useState } from 'react';
import { IReinsurerValues, IReinsurersListResponse } from '.';
import { drawerSections } from './content';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { toast } from 'react-toastify';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  getReinsurersListQuery,
  getTreatyReinsurersQuery,
  updateTreatyReinsurersMutation,
} from './queries';
import { useParams } from 'react-router-dom';
import { getError, lookupListAsRecordObject } from '@utils/graph-utils';
import { IAbstractRecord, IPagination } from '@models';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { cloneDeep } from 'lodash';

interface IUpdateReinsurersDrawer {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const UpdateReinsurersDrawer: React.FC<IUpdateReinsurersDrawer> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { id: currentTreatyId } = useParams<{ id: string }>();
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [values, setValues] = useState<IReinsurerValues[]>([
    {
      reinsurer: '',
      share: 0,
    },
  ]);
  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>(
    drawerSections(values)
  );

  const { data } = useQuery(getTreatyReinsurersQuery, {
    variables: {
      TreatyID: currentTreatyId,
    },
  });
  const [getReinsurersLazy] = useLazyQuery(getReinsurersListQuery);
  const [updateTreatyReinsurers] = useMutation(updateTreatyReinsurersMutation);

  // this is used to keep track of the selected reinsurers names
  const reinsurersList = useRef<Record<string, string>>({});
  const updateReinsurersList = (options: IAbstractRecord) => {
    // merge the new options with the existing ones and keep them unique
    reinsurersList.current = {
      ...reinsurersList.current,
      ...options,
    };
  };

  const getReinsurers = async () => {
    try {
      const newValues: IReinsurerValues[] = [];
      data?.Production?.queries?.GetTreatyReinsurers?.forEach(
        (item: IReinsurersListResponse) => {
          newValues.push({
            isLeader: item.production_TreatyReinsurers_IsLeader,
            reinsurer: item.treatyReinsurersBusinessPartner_Id,
            share: item.production_TreatyReinsurers_SharePercentage,
          });
        }
      );

      // sort to sure the leader is always the first item
      newValues.sort((a) => (a.isLeader ? -1 : 1));

      setValues(newValues);
      setInputsForm(cloneDeep(drawerSections(newValues)));
    } catch (error) {
      toast.error(<ToastErrorMessage>{getError(error)}</ToastErrorMessage>);
    }
  };

  useEffect(() => {
    getReinsurers();
  }, [data]);

  const handleSearch = (
    fieldName: string,
    value: string,
    pagination: IPagination,
    hasDefaultValue: boolean
  ) => {
    const defaultValue = hasDefaultValue ? value : null;
    return getReinsurersLazy({
      variables: {
        SelectedCompany: '41',
        KeywordSearch: hasDefaultValue ? null : value,
        pagination,
        ExistingReinsurerID: defaultValue,
      },
    }).then((result) => {
      const options = lookupListAsRecordObject(
        result.data?.Production?.queries?.ReinsurersList?.items,
        false,
        'salesforceManagement_BusinessPartner_Id',
        'salesforceManagement_BusinessPartner_FullName'
      );
      updateReinsurersList(options); // update the list of reinsurers for external use
      return {
        options,
        totalCount:
          result.data?.Production?.queries?.ReinsurersList?.paging?.totalCount,
      };
    });
  };

  const handleChange = (
    fieldName: string,
    value: unknown,
    sectionIndex: number,
    values: { reinsurersRepeater: IReinsurerValues[] }
  ) => {
    const { reinsurersRepeater } = values;
    setValues(reinsurersRepeater);
    setInputsForm(drawerSections(reinsurersRepeater));
  };

  const handleSubmit = async () => {
    try {
      setIsDisabled(true);

      const updatedReinsurers = values.map((item, index) => ({
        IsLeader: index === 0,
        ReinsurerID: item.reinsurer,
        ReinsurerShare: Number(item.share),
        ReinsurerName: reinsurersList.current[item.reinsurer],
      }));

      // Move the first item (leader reinsurer) to the end of the array for the backend to process it correctly
      const finalReinsurers = [
        ...updatedReinsurers.slice(1),
        updatedReinsurers[0],
      ];

      const res = await updateTreatyReinsurers({
        variables: {
          CurrentTreatyID: currentTreatyId,
          ReinsurersList: finalReinsurers,
        },
      });

      if (!res.errors) {
        toast.success(
          <ToastSuccessMessage>
            Reinsurers updated successfully
          </ToastSuccessMessage>
        );
        onClose();
        onSuccess();
      }
    } catch (error) {
      toast.error(<ToastErrorMessage>{getError(error)}</ToastErrorMessage>);
    } finally {
      setIsDisabled(false);
    }
  };

  return (
    <DynamicFormDrawer
      title={'Update Reinsurers'}
      onClose={() => onClose()}
      isOpen={isOpen}
      sections={inputsForm}
      onSubmit={handleSubmit}
      isSubmitButtonDisabled={isDisabled}
      onChange={handleChange}
      onAutocompleteSearch={handleSearch}
    />
  );
};

export default UpdateReinsurersDrawer;
